import { defaultBpm } from "app/singing/vocal-driller/usePlayer/getDefaultExternalState";
import { useCallback, useEffect, useRef, useState } from "react";

export default function useTapTempo(
  initialBpm: number = defaultBpm,
  onChange: (bpm: number) => void,
  { min, max }: { min: number; max: number }
) {
  const [bpm, setBpm] = useState<number>(initialBpm);
  const lastTime = useRef<number | null>(null);
  const times = useRef<number[]>([]);

  const tap = useCallback(() => {
    const time = Date.now();

    // If more than 2 seconds have passed since the last tap, clear the times array
    if (lastTime.current !== null && time - lastTime.current > 2000) {
      times.current = [];
    }

    // Keep times array less then 3 values — remove the oldest value
    if (times.current.length > 2) {
      times.current.shift();
    }

    if (lastTime.current !== null) {
      const difference = time - lastTime.current;
      times.current.push(difference);

      // If the times array has more than 10 values, remove the oldest value
      if (times.current.length > 10) {
        times.current.shift();
      }

      if (times.current.length > 2) {
        const average =
          times.current.reduce((result, t) => (result += t)) /
          times.current.length;
        const newBpm = (1 / (average / 1000)) * 60;

        if (newBpm < min) {
          return setBpm(min);
        }

        if (newBpm > max) {
          return setBpm(max);
        }

        setBpm(newBpm);
      }
    }

    lastTime.current = time;
  }, [max, min]);

  useEffect(() => {
    onChange(bpm);
  }, [bpm, onChange]);

  const set = useCallback(
    (bpm: number) => {
      if (bpm < min) {
        return setBpm(min);
      }

      if (bpm > max) {
        return setBpm(max);
      }

      setBpm(bpm);
    },
    [max, min]
  );

  return { tap, bpm: bpm.toFixed(0), set };
}
